import { Grid, Skeleton, Typography } from '@mui/material';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { useLanguageCode } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { AstroUserProfile } from '@/generated/gql/graphql';
import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { CuriosityInfoChip } from '~/components/CuriosityInfoChip/CuriosityInfoChip';
import { ListEmptyFallback } from '~/components/ListEmptyFallback/ListEmptyFallback';

import { StackStyled } from './UserProfileContent.styled';

export interface UserProfileContentProps {
  data?: AstroUserProfile;
  loading?: boolean;
}

export const UserProfileContent = ({ data, loading }: UserProfileContentProps) => {
  const { languageCode } = useLanguageCode();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserProfileDetailsWidget' });
  const { t: tLang } = useTranslation(I18nNamespace.common, { keyPrefix: 'language' });

  return (
    <Grid
      container
      direction="row"
      spacing={4}
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        alignContent: 'flex-start',
      }}
    >
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('workingHours')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Typography variant="subtitle2" color="textPrimary">
          {loading ? <Skeleton /> : <>{t('hours', { count: data?.workingHours ?? 0 })}</>}
        </Typography>
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('commodityGroupIds')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {loading ? (
          <Skeleton />
        ) : (
          <ListEmptyFallback component={StackStyled} direction="row">
            {data?.commodityGroupIds.map((item, index) => (
              <CuriosityInfoChip key={item ?? index} ns={CuriosityNamespaceType.COMMODITY_GROUP} i18nKey={item} />
            ))}
          </ListEmptyFallback>
        )}
      </Grid>
      <Grid
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('capabilityIds')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {loading ? (
          <Skeleton />
        ) : (
          <ListEmptyFallback component={StackStyled} direction="row">
            {data?.capabilityIds.map((item, index) => (
              <CuriosityInfoChip key={item ?? index} ns={CuriosityNamespaceType.CAPABILITY} i18nKey={item} />
            ))}
          </ListEmptyFallback>
        )}
      </Grid>
      <Grid
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('organizationId')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {loading ? (
          <Skeleton />
        ) : data?.organizationId ? (
          <CuriosityInfoChip ns={CuriosityNamespaceType.ORGANIZATION} i18nKey={data?.organizationId} />
        ) : (
          NULLISH_VALUE
        )}
      </Grid>
      <Grid
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('deliveryLocationId')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {loading ? (
          <Skeleton />
        ) : data?.deliveryLocationId ? (
          <CuriosityInfoChip ns={CuriosityNamespaceType.LOCATION} i18nKey={data?.deliveryLocationId} />
        ) : (
          NULLISH_VALUE
        )}
      </Grid>
      <Grid
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('accountingAreaId')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {loading ? (
          <Skeleton />
        ) : data?.accountingAreaId ? (
          <CuriosityInfoChip ns={CuriosityNamespaceType.ACCOUNTING_AREA} i18nKey={data?.accountingAreaId} />
        ) : (
          NULLISH_VALUE
        )}
      </Grid>
      <Grid
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('currency')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>{loading ? <Skeleton /> : data?.currency ? data?.currency : NULLISH_VALUE}</Grid>{' '}
      <Grid
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('language')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {loading ? <Skeleton /> : data?.language ? tLang(data?.language.toLowerCase()) : tLang(languageCode)}
      </Grid>
    </Grid>
  );
};
